import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  TournamentPresentialBadge,
  TournamentStatusBadge,
} from "@/components/ui/TournamentStatusBadge"
import { Calendar, Cog, ExternalLink, MapPin, Trophy, Users } from "lucide-react"
import { Link } from "react-router-dom"

import { CountryFlag } from "@/components/ui/country-flag"
import { useCountries } from "@/graphql"
import { formatDate } from "./utils"

export const TournamentInfo = ({ tournament }) => {
  const { countriesMap } = useCountries()
  if (!tournament) return null

  return (
    <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 overflow-hidden">
      <CardContent className="p-0">
        {/* Header strip */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4 pb-3 border-b border-muted/40 dark:border-gray-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Detalles del Torneo
            </span>
            <TournamentStatusBadge statusId={tournament.statusId} className="dark:text-gray-200" />
            <TournamentPresentialBadge
              state={tournament.isPresential}
              className="dark:text-gray-200"
            />
          </div>

          {tournament.statusId >= 3 ? (
            <Button variant="default" size="sm" asChild>
              <Link to={tournament.urlRounds}>
                <Trophy className="h-3.5 w-3.5 mr-1.5" />
                Ver Rondas y Resultados
              </Link>
            </Button>
          ) : (
            <Button variant="secondary" size="sm" disabled>
              <Trophy className="h-3.5 w-3.5 mr-1.5" />
              Rondas y Resultados
            </Button>
          )}
        </div>

        {/* Details grid */}
        <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-400">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Fecha:</span>
            <span>{formatDate(tournament.startDate)}</span>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-400">
            <Cog className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Formato:</span>
            <span>{tournament.validDates === 1 ? "Simple" : "Doble"}</span>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-400">
            <Trophy className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Rondas:</span>
            <span>{tournament.rounds}</span>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-400">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Modalidad:</span>
            <span>{tournament.isPresential ? "Presencial" : "Online"}</span>
          </div>

          {tournament.maxPlayers > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-400">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span className="font-medium text-gray-900 dark:text-gray-100">Máx. jugadores:</span>
              <span>{tournament.maxPlayers}</span>
            </div>
          )}
        </div>

        {/* Address */}
        {tournament.Address && (
          <div className="flex items-start gap-2 px-4 pb-3 border-t border-muted/30 dark:border-gray-700 pt-3">
            <CountryFlag countryCode={countriesMap.get(tournament.Address?.countryId)?.iso} />
            <p className="text-xs text-muted-foreground dark:text-gray-400 leading-relaxed">
              <span className="font-medium text-gray-900 dark:text-gray-100 block">
                {tournament.Address.name}
              </span>
              {tournament.Address.address && (
                <span>
                  {tournament.Address.address}
                  <br />
                </span>
              )}
              {tournament.Address.Location && (
                <span>
                  {tournament.Address.Location}, {tournament.Address.Region}
                  <br />
                </span>
              )}
              {tournament.Address.Country}
            </p>
          </div>
        )}

        {/* External link */}
        {tournament.eventUrl && (
          <div className="px-4 pb-4 pt-1">
            <Button variant="outline" size="sm" asChild>
              <a href={tournament.eventUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Link externo
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
